import { BooLogo } from "@/components/BooLogo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { api, type AppInfo } from "@/lib/api";
import { buildCategoryTree } from "@/lib/hub-catalog";
import { useCatalog, useCategories } from "@/lib/use-hub-catalog";
import { connectedForBoo, hubAccountsKey } from "@/lib/hub-accounts";
import { useBooCanConfigure } from "@/lib/use-boo-configurable";
import { cn } from "@/lib/utils";
import {
  CatalogTile,
  CategoryMenu,
  CategoryRail,
  type BrowserView,
} from "@/pages/container-tabs/integrations/HubApps";
import { useDebounced } from "@/hooks/useDebounced";
import {
  CONNECT_TARGET,
  connectedByApp,
  useConnectApp,
} from "@/pages/container-tabs/integrations/hub-lib";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  beginCatalogConnect,
  endCatalogConnect,
  markCatalogEngaged,
} from "./catalog-activity";
import { Step3Preview } from "./previews/Step3Preview";

// The provisioning wait, split in two:
//   - ProvisioningPitch (left column, under the spinner): the "connect 3 or
//     more" push with a progress row. Copy is a placeholder per BOO product
//     direction — tune freely.
//   - OnboardingCatalogPane (right pane, replacing the illustration): a real
//     catalog browser — category rail, search, tiles — headlined by the
//     1,000+ integrations claim.
// Accounts connected here are private to the Boo being set up (containerId
// from the onboarding state) and bound to it, so they're usable the moment
// it's ready and no other Boo sees them. A connect leaves the page for the
// hub's hosted flow and returns to /onboarding/provisioning with the outcome
// (ProvisioningStep reads it, stays put, and refetches). Both sides read the
// same hub-accounts query, so a connect advances the progress row.

// Where a connect started on this step comes back to.
export const PROVISIONING_RETURN_TO = "/onboarding/provisioning";

export function ProvisioningPitch({ botName, containerId }: { botName: string; containerId?: string }) {
  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
    enabled: !!containerId,
    retry: false,
  });
  // Connecting for the Boo being set up needs its configure permission (its
  // creator owns it), not an org role.
  const canConnect = useBooCanConfigure(containerId);

  // The onboarding user owns the Boo they're setting up, but if the accounts
  // fetch is refused, the hub is unreachable, or they can't configure the Boo,
  // the pitch quietly disappears — this screen's job is the wait, not the
  // catalog.
  if (!containerId || accountsQuery.isError || canConnect === false) return null;

  const count = connectedForBoo(accountsQuery.data?.accounts ?? [], containerId).length;
  const done = Math.min(count, CONNECT_TARGET);

  return (
    <div className="space-y-3 rounded-lg border p-4 text-left">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">Connect 3 or more integrations</h2>
        <p className="text-sm text-muted-foreground">
          Each one on its own is a tool you already have. The power of Boo is
          stitching them together. Connect three or more and you&apos;ll see
          what {botName} can do almost instantly.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <TargetDots done={done} />
        <span className="pl-1 text-xs text-muted-foreground tabular-nums">
          {done >= CONNECT_TARGET
            ? `${botName} is wired up. Keep going if you like.`
            : `${count} of ${CONNECT_TARGET} connected`}
        </span>
      </div>
      {/* The bold, focal action: it takes over the whole screen with the
          browse experience. On every viewport — desktop included — the
          catalog lives behind this button as a full-screen takeover, not in a
          side pane, so the button is always the way in. */}
      <CatalogTakeover containerId={containerId} />
    </div>
  );
}

// A "Browse integrations" trigger that opens the catalog as a full-screen
// takeover sliding up from the bottom. Shown on every viewport. Closable by
// the X, the backdrop, Escape, and the browser back button — the last via a
// pushed history entry that popstate unwinds, so back dismisses the takeover
// instead of leaving onboarding. The teammate pitch uses the bold button; the
// chief-of-staff inbox card uses the quiet link under its curated tiles.
export function CatalogTakeover({
  trigger = "button",
  containerId,
}: {
  trigger?: "button" | "link";
  containerId?: string;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    window.history.pushState({ booCatalog: true }, "");
    const onPop = () => setOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setOpen(true);
      return;
    }
    // Close via X / backdrop / Escape: pop our pushed entry so the back
    // button and these controls share one history slot (popstate closes it).
    if (window.history.state?.booCatalog) window.history.back();
    else setOpen(false);
  };

  return (
    <>
      {trigger === "link" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Browse all integrations
        </button>
      ) : (
        <Button className="w-full" onClick={() => setOpen(true)}>
          Browse integrations
        </Button>
      )}
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          // `!h-` beats the sheet's own `data-[side=bottom]:h-auto` (attribute
          // selector, higher specificity) so it fills the screen instead of
          // sizing to content, which pushed the close button off-viewport.
          className="!h-[100dvh] gap-0 rounded-none border-0 p-0"
        >
          <SheetTitle className="sr-only">Browse integrations</SheetTitle>
          {/* Centered, capped width so the catalog doesn't stretch edge to
              edge on wide desktops. */}
          <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col p-4 sm:p-6">
            <OnboardingCatalogPane bare containerId={containerId} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// The three connect-target circles, shared by the pitch card and the
// continue nudge dialog so progress reads the same everywhere.
function TargetDots({ done }: { done: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: CONNECT_TARGET }, (_, i) => (
        <span
          key={i}
          className={cn(
            "flex size-7 items-center justify-center rounded-full border text-xs font-medium tabular-nums transition-colors",
            i < done
              ? "border-emerald-300 bg-emerald-50 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400"
              : "border-dashed text-muted-foreground/60",
          )}
        >
          {i < done ? <Check className="size-3.5" /> : i + 1}
        </span>
      ))}
    </div>
  );
}

// Success header shown in place of the spinner once the backend says the Boo
// is ready. Deliberately CTA-free: connecting integrations is the action we
// want, so the catalog carries the attention and the only way forward
// ("I'll connect later") sits below the pitch as a quiet link — see
// ConnectLater. The subtext points at connecting, not at leaving.
export function ProvisioningReady({ botName }: { botName: string }) {
  return (
    <div className="flex flex-col items-center gap-6 py-8 text-center">
      <div className="relative">
        <BooLogo className="size-20 rounded-2xl" />
        <span className="absolute -right-1.5 -bottom-1.5 flex size-7 items-center justify-center rounded-full border-2 border-background bg-emerald-500 text-white">
          <Check className="size-4" />
        </span>
      </div>
      <div className="max-w-sm space-y-2">
        <h1 className="text-2xl font-semibold">{botName} is ready</h1>
        <p className="text-sm text-muted-foreground">
          Connect a few tools below and {botName} can get to work the moment
          you&apos;re done.
        </p>
      </div>
    </div>
  );
}

// The low-prominence way forward, placed below the connect pitch so the value
// is read first. A quiet "I'll connect later" link, not a primary button —
// connecting is the goal, this is just the escape hatch. If they try to leave
// below the target, one value-reinforcing nudge offers a chance to keep going;
// it re-appears only when they've made progress since it last showed (capped
// at MAX_NUDGES) so it can never loop or nag. Skipped at 3+, on hub errors, or
// when the user can't connect for this Boo.
export function ConnectLater({
  botName,
  containerId,
  onContinue,
}: {
  botName: string;
  containerId?: string;
  onContinue: () => void;
}) {
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [lastShownAt, setLastShownAt] = useState<number | null>(null);
  const [shownCount, setShownCount] = useState(0);
  const MAX_NUDGES = 2;
  // "Keep connecting" is the intended default: Radix focuses the first
  // focusable child on open (the ghost "Continue anyway"), which would make
  // Enter dismiss the nudge — the opposite of the point. Focus the primary
  // action explicitly instead.
  const keepConnectingRef = useRef<HTMLButtonElement>(null);

  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
    enabled: !!containerId,
    retry: false,
  });
  const canConnect = useBooCanConfigure(containerId);
  const canNudge = accountsQuery.isSuccess && canConnect === true;
  const count = containerId
    ? connectedForBoo(accountsQuery.data?.accounts ?? [], containerId).length
    : 0;
  const remaining = Math.max(0, CONNECT_TARGET - count);

  const handleSkip = () => {
    const madeProgress = lastShownAt === null || count > lastShownAt;
    if (canNudge && remaining > 0 && shownCount < MAX_NUDGES && madeProgress) {
      setLastShownAt(count);
      setShownCount((n) => n + 1);
      setNudgeOpen(true);
      return;
    }
    onContinue();
  };

  // Once they've hit the target, "later" is wrong — they've connected. The
  // link becomes a plain forward step instead of an escape hatch.
  const skipLabel = count >= CONNECT_TARGET ? "Done connecting" : "I'll connect later";

  return (
    <div className="flex justify-center">
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
        onClick={handleSkip}
      >
        {skipLabel}
      </Button>

      <Dialog open={nudgeOpen} onOpenChange={setNudgeOpen}>
        <DialogContent
          className="sm:max-w-md"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            keepConnectingRef.current?.focus();
          }}
        >
          <DialogHeader className="gap-3">
            <DialogTitle>
              {count === 0
                ? "Connect a few integrations"
                : remaining === 1
                  ? "One more to go"
                  : "Two more to go"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                {count === 0 ? (
                  <>
                    <p>Each one on its own is a tool you already have.</p>
                    <p>
                      Connect three or more and {botName} can start stitching
                      them together from day one.
                    </p>
                  </>
                ) : (
                  <>
                    <p>You&apos;ve connected {count} so far.</p>
                    <p>
                      The real payoff shows up when {botName} stitches several
                      tools together. {remaining === 1 ? "One more" : "Two more"}{" "}
                      and you&apos;ll see it almost instantly.
                    </p>
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <TargetDots done={Math.min(count, CONNECT_TARGET)} />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setNudgeOpen(false);
                onContinue();
              }}
            >
              Continue anyway
            </Button>
            <Button ref={keepConnectingRef} onClick={() => setNudgeOpen(false)}>
              Keep connecting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// bare = drop the card chrome (border/rounded/bg/padding) so the full-screen
// mobile sheet supplies the surface instead. Default keeps the panel look for
// the desktop right pane.
export function OnboardingCatalogPane({
  bare = false,
  containerId,
}: {
  bare?: boolean;
  containerId?: string;
} = {}) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<BrowserView>({ kind: "all" });
  const debouncedQuery = useDebounced(query.trim(), 250);

  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(containerId),
    queryFn: () => api.integrations.accounts(containerId),
    enabled: !!containerId,
    retry: false,
  });
  const canConnect = useBooCanConfigure(containerId);
  const { apps, loading, error, hasMore, loadMore } = useCatalog({
    query: debouncedQuery,
    category: view.kind === "category" ? view.slug : undefined,
    // App-connected apps are set up on the Boo's Connections.
    connectedBy: "hub",
    pageSize: 24,
  });
  const { connect: startConnect, connecting } = useConnectApp({
    containerId,
    returnTo: PROVISIONING_RETURN_TO,
  });
  // Report connect flights to the shared activity store so the provisioning
  // step never auto-advances while the link is being minted. startConnect
  // handles its own errors and always resolves (or the page has left).
  const connect = async (app: AppInfo) => {
    beginCatalogConnect();
    try {
      await startConnect(app);
    } finally {
      endCatalogConnect();
    }
  };
  const { categories, loading: categoriesLoading } = useCategories();
  const tree = buildCategoryTree(categories);
  const connected = connectedByApp(
    containerId ? connectedForBoo(accountsQuery.data?.accounts ?? [], containerId) : [],
  );
  const pickView = (next: BrowserView) => {
    markCatalogEngaged();
    setView(next);
  };

  // No Boo yet, hub unreachable, or not allowed to connect for it → fall back
  // to the step illustration instead of a broken half-pane.
  const gated = !containerId || accountsQuery.isError || canConnect === false || error !== null;
  if (gated) return <Step3Preview />;

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4",
        bare
          ? ""
          : "max-h-[calc(100vh-2rem)] rounded-2xl border bg-background p-4 sm:p-6",
      )}
    >
      <div>
        <h2 className="text-lg font-semibold">Browse 1,000+ integrations</h2>
        <p className="text-sm text-muted-foreground">
          Everything your Boo can plug into: email, docs, calendars, CRMs,
          and more. One click to connect.
        </p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        {/* The rail needs a wide viewport; below lg the dropdown steps in. */}
        <div className="lg:hidden">
          <CategoryMenu connectedCount={0} view={view} onPick={pickView} showConnected={false} />
        </div>
        <Input
          placeholder="Search integrations…"
          value={query}
          onChange={(e) => {
            markCatalogEngaged();
            setQuery(e.target.value);
          }}
          className="sm:flex-1"
        />
      </div>

      <div className="flex min-h-0 flex-1 items-start gap-6">
        {/* Connected is hidden during onboarding — progress lives in the
            left pitch column, not the rail. */}
        <CategoryRail
          tree={tree}
          loading={categoriesLoading}
          connectedCount={0}
          view={view}
          onPick={pickView}
          showConnected={false}
        />

        <div className="h-full min-w-0 flex-1 overflow-y-auto">
          {loading && apps.length === 0 ? (
            <div className="flex flex-col gap-2" aria-hidden>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : apps.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">
              {query.trim() !== ""
                ? "No integrations match your search."
                : "No integrations in this category yet."}
            </p>
          ) : (
            <div className="flex flex-col gap-2 pb-2">
              {apps.map((app) => (
                <CatalogTile
                  key={app.slug}
                  app={app}
                  connectedCount={connected.get(app.slug) ?? 0}
                  busy={connecting === app.slug}
                  disabled={connecting !== null}
                  onConnect={() => void connect(app)}
                />
              ))}
              {hasMore && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  disabled={loading}
                  onClick={loadMore}
                >
                  {loading ? "Loading…" : "Load more"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
